import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { haeuserApi, HAUS_TYPEN, type HausUpdateData } from '../api/haeuser';
import { adressenApi } from '../api/adressen';
import { anfragenApi } from '../api/anfragen';
import VerfahrenStateBadge from '../components/VerfahrenStateBadge';
import InlineEditField from '../components/InlineEditField';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import MapLinkButton from '../components/MapLinkButton';
import { restoreOne } from '../api/softDelete';
import { useCan } from '../hooks/useCan';
import { useIsModuleEnabled } from '../hooks/useConfigVisibility';

export default function HausDetail() {
  const { hausId } = useParams<{ hausId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showAdressen = useIsModuleEnabled('adressen');
  const showObjekte = useIsModuleEnabled('objekte');
  const showVerfahren = useIsModuleEnabled('verfahren');
  const showAnfragen = useIsModuleEnabled('anfragen');
  const showInserate = useIsModuleEnabled('inserate');
  const showVermieter = useIsModuleEnabled('vermieter');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Duplicate warning state: triggered when a 409 is returned on update
  const [duplicateWarning, setDuplicateWarning] = useState<{
    field: 'bezeichnung' | 'typ';
    message: string;
    pendingValue: string;
  } | null>(null);
  const [duplicateBezeichnung, setDuplicateBezeichnung] = useState('');

  const { data: haus, isLoading, error } = useQuery({
    queryKey: ['haus', hausId],
    queryFn: () => haeuserApi.getHaus(Number(hausId)),
    enabled: !!hausId,
  });

  const { data: adresse } = useQuery({
    queryKey: ['adresse', haus?.adresse_id],
    queryFn: () => adressenApi.getAdresse(haus!.adresse_id),
    enabled: !!haus?.adresse_id,
  });

  const { data: anfragenList } = useQuery({
    queryKey: ['haus', hausId, 'anfragen'],
    queryFn: () => anfragenApi.getAnfragenAtHaus(Number(hausId)),
    enabled: !!hausId,
  });

  const { data: verfahrenList } = useQuery({
    queryKey: ['haus', hausId, 'verfahren'],
    queryFn: () => haeuserApi.getVerfahrenAtHaus(Number(hausId)),
    enabled: !!hausId,
  });

  const { data: inseratList } = useQuery({
    queryKey: ['haus', hausId, 'inserate'],
    queryFn: () => haeuserApi.getInserate(Number(hausId)),
    enabled: !!hausId,
  });

  const { data: vermieterList } = useQuery({
    queryKey: ['haus', hausId, 'vermieter'],
    queryFn: () => haeuserApi.getVermieter(Number(hausId)),
    enabled: !!hausId,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: HausUpdateData) =>
      haeuserApi.updateHaus(Number(hausId), updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['haus', hausId] });
      setDuplicateWarning(null);
    },
    onError: (err: any) => {
      const detail: string = err?.response?.data?.detail ?? '';
      if (err?.response?.status === 409) {
        setDuplicateWarning({
          field: 'bezeichnung',
          message: detail,
          pendingValue: '',
        });
        setDuplicateBezeichnung(haus?.bezeichnung ?? '');
      }
    },
  });

  const canRestore = useCan('restore_own');
  const restoreMutation = useMutation({
    mutationFn: () => restoreOne('haus', Number(hausId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['haus', hausId] });
      queryClient.invalidateQueries({ queryKey: ['haeuser'] });
    },
  });

  const handleSave = (field: string, value: string | null) => {
    updateMutation.mutate({ [field]: value || null });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !haus) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600">Fehler</h2>
          <p className="mt-2 text-gray-600">Haus konnte nicht geladen werden.</p>
          <button onClick={() => navigate('/haeuser')} className="mt-4 text-blue-600 hover:text-blue-800">
            Zurück zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  const headerTitle = [haus.typ, haus.bezeichnung].filter(Boolean).join(' – ') || `Haus #${haus.id}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/haeuser')} className="text-blue-600 hover:text-blue-800 text-sm mb-2">
            &larr; Zurück zur Übersicht
          </button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{headerTitle}</h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {haus.typ && (
                  <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
                    {haus.typ}
                  </span>
                )}
                {adresse?.latitude != null && adresse?.longitude != null && (
                  <MapLinkButton to={`/karte?haus=${haus.id}`} />
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {haus.deleted_at ? (
                <>
                  <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-slate-200 text-slate-700">
                    Ausgeblendet
                  </span>
                  {canRestore && (
                    <button
                      onClick={() => restoreMutation.mutate()}
                      disabled={restoreMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100"
                    >
                      Wiederherstellen
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                >
                  Ausblenden
                </button>
              )}
            </div>
          </div>
          {adresse && (
            <p className="text-sm text-gray-500 mt-1">
              <button
                onClick={() => navigate(`/adressen/${adresse.id}`)}
                className="text-blue-600 hover:underline"
              >
                {adresse.strasse} {adresse.hausnummer}
              </button>
              {adresse.plz && `, ${adresse.plz}`}
              {adresse.stadt && ` ${adresse.stadt}`}
            </p>
          )}
        </div>
      </header>

      {/* Duplicate warning modal */}
      {duplicateWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-medium text-amber-700 mb-2">Duplikat erkannt</h3>
            <p className="text-sm text-gray-600 mb-4">
              Ein Haus mit diesem Typ und dieser Bezeichnung existiert bereits. Bitte gib eine andere Bezeichnung ein, um dieses Haus eindeutig zu kennzeichnen.
            </p>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 mb-1">Neue Bezeichnung</label>
              <input
                type="text"
                value={duplicateBezeichnung}
                onChange={(e) => setDuplicateBezeichnung(e.target.value)}
                placeholder="z.B. Vorderhaus, Gebäude B"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                autoFocus
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDuplicateWarning(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Abbrechen
              </button>
              <button
                onClick={() => updateMutation.mutate({ bezeichnung: duplicateBezeichnung || null })}
                disabled={updateMutation.isPending || !duplicateBezeichnung.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {updateMutation.isPending ? 'Wird gespeichert...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        entity="haus"
        ids={[Number(hausId)]}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['haeuser'] });
          navigate('/haeuser');
        }}
      />

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Hausdaten */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">Hausdaten</h3>
          </div>
          <div className="px-6">
            <dl className="divide-y divide-gray-200">
              {/* Typ – dropdown */}
              <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-gray-500">Typ</dt>
                <dd className="mt-1 sm:mt-0 sm:col-span-2">
                  <select
                    value={haus.typ ?? ''}
                    onChange={(e) => handleSave('typ', e.target.value)}
                    className="w-full bg-transparent border border-transparent rounded px-2 py-1 text-sm text-gray-900 hover:border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-colors"
                  >
                    <option value="">– Typ wählen –</option>
                    {HAUS_TYPEN.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </dd>
              </div>

              <InlineEditField
                label="Bezeichnung"
                value={haus.bezeichnung ?? ''}
                placeholder="Optional, z.B. Vorderhaus"
                onSave={(v) => handleSave('bezeichnung', v)}
              />
              <InlineEditField
                label="Baugenehmigung"
                value={haus.baugenehmigung ?? ''}
                placeholder="Nicht angegeben"
                onSave={(v) => handleSave('baugenehmigung', v)}
              />
              <InlineEditField
                label="Notizen"
                value={haus.notes ?? ''}
                placeholder="Keine Notizen"
                onSave={(v) => handleSave('notes', v)}
                multiline
              />
            </dl>
          </div>
          {updateMutation.isError && !duplicateWarning && (
            <div className="px-6 pb-4">
              <p className="text-sm text-red-600">
                {(updateMutation.error as any)?.response?.data?.detail || 'Fehler beim Speichern'}
              </p>
            </div>
          )}
        </div>

        {/* Adresse */}
        {adresse && showAdressen && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">Adresse</h3>
            </div>
            <div className="px-6 py-4">
              <button
                onClick={() => navigate(`/adressen/${adresse.id}`)}
                className="text-blue-600 hover:underline font-medium"
              >
                {adresse.strasse} {adresse.hausnummer}
              </button>
              {(adresse.plz || adresse.stadt) && (
                <p className="text-sm text-gray-500 mt-1">
                  {[adresse.plz, adresse.stadt].filter(Boolean).join(' ')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Objekte (Einheiten) */}
        {showObjekte && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">Objekte ({haus.einheiten?.length || 0})</h3>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bezeichnung</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Typ</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reg.-Nr.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {haus.einheiten?.map((e) => (
                <tr
                  key={e.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/objekte/${e.id}`)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-blue-600">{e.bezeichnung}</td>
                  <td className="px-4 py-3 text-sm">
                    {e.typ ? (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-teal-100 text-teal-800">
                        {e.typ}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{e.registrierungsnummer || '-'}</td>
                </tr>
              ))}
              {(!haus.einheiten || haus.einheiten.length === 0) && (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                    Keine Objekte in diesem Haus.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Verfahren */}
        {showVerfahren && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">Verfahren ({verfahrenList?.length || 0})</h3>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktenzeichen</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Objekt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {verfahrenList?.map((v) => (
                <tr
                  key={v.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/verfahren/${v.id}`)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-blue-600">
                    {v.aktenzeichen || `#${v.id}`}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <VerfahrenStateBadge staat={v.staat} small />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{v.einheit_bezeichnung || '-'}</td>
                </tr>
              ))}
              {(!verfahrenList || verfahrenList.length === 0) && (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                    Keine Verfahren an diesem Haus.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Anfragen */}
        {showAnfragen && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">Anfragen ({anfragenList?.length || 0})</h3>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notizen</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Erstellt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {anfragenList?.map((a) => (
                <tr
                  key={a.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/anfragen/${a.id}`)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-blue-600">
                    {a.adresse_text || `#${a.id}`}
                    {a.zipcode && <span className="text-gray-500 font-normal ml-1">{a.zipcode}</span>}
                    {a.city && <span className="text-gray-500 font-normal ml-1">{a.city}</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{a.notes || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                    {new Date(a.created_at).toLocaleDateString('de-DE')}
                  </td>
                </tr>
              ))}
              {(!anfragenList || anfragenList.length === 0) && (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                    Keine Anfragen an diesem Haus.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Inserate */}
        {showInserate && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">Inserate ({inseratList?.length || 0})</h3>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titel</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plattform</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Online</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {inseratList?.map((i) => (
                <tr
                  key={i.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/inserate/${i.id}`)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-blue-600 max-w-xs truncate">{i.title}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 capitalize">{i.platform}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                      {i.state}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {i.is_online ? (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">Online</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">Offline</span>
                    )}
                  </td>
                </tr>
              ))}
              {(!inseratList || inseratList.length === 0) && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    Keine Inserate an diesem Haus.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Vermieter */}
        {showVermieter && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">Vermieter ({vermieterList?.length || 0})</h3>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Typ</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-Mail</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefon</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {vermieterList?.map((v) => (
                <tr
                  key={v.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/vermieter/${v.id}`)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-blue-600">{v.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{v.typ || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{v.email || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{v.telefon || '-'}</td>
                </tr>
              ))}
              {(!vermieterList || vermieterList.length === 0) && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    Keine Vermieter an diesem Haus.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </main>
    </div>
  );
}
